import { useState } from 'react';
import { CalendarHolidayItem, WorkCalendarSummary } from '../../../services/apiClient';
import { Icon } from '../../ui/Icon';
import { EmptyState } from '../../ui/EmptyState';

const TYPE_LABELS: Record<string, string> = {
  NATIONAL: 'Nacional',
  REGIONAL: 'Autonómico',
  LOCAL: 'Local',
  COMPANY: 'Convenio',
};

const EMPTY_HOLIDAY_DRAFT = { date: '', name: '', holiday_type: 'LOCAL' as 'LOCAL' | 'COMPANY' };

interface HolidayPanelProps {
  selected: WorkCalendarSummary | null;
  holidays: CalendarHolidayItem[];
  loadingHolidays: boolean;
  onAdd: (data: { date: string; name: string; holiday_type: 'LOCAL' | 'COMPANY' }) => Promise<void>;
  onEdit: (holidayId: number, data: { date: string; name: string }) => Promise<void>;
  onDelete: (holiday: CalendarHolidayItem) => Promise<void>;
  onClearError: () => void;
}

/**
 * Panel derecho: festivos del calendario seleccionado — tabla, alta de
 * festivo (local o de convenio) y edición inline (fecha incluida). Todo el
 * estado de estos sub-formularios es local; el orquestador lo resetea al
 * cambiar de calendario montando este componente con `key={selectedId}`, así
 * que aquí no hace falta ningún efecto de limpieza.
 */
export function HolidayPanel({
  selected,
  holidays,
  loadingHolidays,
  onAdd,
  onEdit,
  onDelete,
  onClearError,
}: HolidayPanelProps) {
  const [showAddHoliday, setShowAddHoliday] = useState(false);
  const [holidayDraft, setHolidayDraft] = useState(EMPTY_HOLIDAY_DRAFT);
  const [addingHoliday, setAddingHoliday] = useState(false);

  const [editingHolidayId, setEditingHolidayId] = useState<number | null>(null);
  const [editDate, setEditDate] = useState('');
  const [editName, setEditName] = useState('');
  const [savingHolidayId, setSavingHolidayId] = useState<number | null>(null);
  const [deletingHolidayId, setDeletingHolidayId] = useState<number | null>(null);

  const closeEditHoliday = () => {
    setEditingHolidayId(null);
    setEditDate('');
    setEditName('');
  };

  const openEditHoliday = (h: CalendarHolidayItem) => {
    setShowAddHoliday(false);
    onClearError();
    setEditingHolidayId(h.id);
    setEditDate(h.date);
    setEditName(h.name);
  };

  const submitAddHoliday = async () => {
    if (!holidayDraft.date || !holidayDraft.name.trim()) return;
    setAddingHoliday(true);
    try {
      await onAdd({
        date: holidayDraft.date,
        name: holidayDraft.name.trim(),
        holiday_type: holidayDraft.holiday_type,
      });
      setShowAddHoliday(false);
      setHolidayDraft(EMPTY_HOLIDAY_DRAFT);
    } catch {
      // 409 u otro error: el orquestador ya lo ha mostrado en el banner.
    } finally {
      setAddingHoliday(false);
    }
  };

  const saveHolidayEdit = async () => {
    if (editingHolidayId == null) return;
    setSavingHolidayId(editingHolidayId);
    try {
      await onEdit(editingHolidayId, { date: editDate, name: editName });
      closeEditHoliday();
    } catch {
      // 409: la fecha destino ya está ocupada por otro festivo del calendario.
    } finally {
      setSavingHolidayId(null);
    }
  };

  const removeHoliday = async (h: CalendarHolidayItem) => {
    setDeletingHolidayId(h.id);
    try {
      await onDelete(h);
    } finally {
      setDeletingHolidayId(null);
    }
  };

  if (!selected) {
    return (
      <EmptyState
        icon="calendar"
        title="Selecciona un calendario"
        body="Elige un calendario de la lista para ver y editar sus festivos."
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => {
            closeEditHoliday();
            setShowAddHoliday((v) => !v);
            onClearError();
          }}
          className="h-[42px] px-5 rounded-xl border border-line-strong font-semibold text-[14px] flex items-center gap-2"
        >
          <Icon name="plus" size={16} strokeWidth={2} />
          {showAddHoliday ? 'Cerrar' : 'Añadir festivo'}
        </button>
      </div>

      {showAddHoliday && (
        <div className="card-surface p-5 space-y-3.5 animate-rise">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5">
            <div>
              <label className="label">Fecha</label>
              <input
                type="date"
                className="field font-mono"
                value={holidayDraft.date}
                onChange={(e) => setHolidayDraft((d) => ({ ...d, date: e.target.value }))}
              />
            </div>
            <div>
              <label className="label">Nombre</label>
              <input
                className="field"
                value={holidayDraft.name}
                onChange={(e) => setHolidayDraft((d) => ({ ...d, name: e.target.value }))}
                placeholder="p. ej. Fiesta mayor local"
              />
            </div>
            <div>
              {/* Locales y de convenio se dan de alta a mano aquí (nacionales
                  y autonómicos, con el sembrado): el tipo viaja al backend,
                  que lo acepta libre en `HolidayCreate`. Antes el alta
                  hardcodeaba LOCAL y la fiesta patronal del convenio nacía
                  mal etiquetada (r2b20[5]). */}
              <label className="label">Tipo</label>
              <select
                className="field"
                value={holidayDraft.holiday_type}
                onChange={(e) =>
                  setHolidayDraft((d) => ({
                    ...d,
                    holiday_type: e.target.value as 'LOCAL' | 'COMPANY',
                  }))
                }
              >
                <option value="LOCAL">Local</option>
                <option value="COMPANY">Convenio</option>
              </select>
            </div>
          </div>
          <button
            type="button"
            onClick={submitAddHoliday}
            disabled={!holidayDraft.date || !holidayDraft.name.trim() || addingHoliday}
            className="btn-primary h-12 max-w-[280px] text-[15px]"
          >
            {addingHoliday ? 'Añadiendo…' : 'Añadir festivo'}
          </button>
        </div>
      )}

      <div className="rounded-2xl bg-surface border border-line overflow-x-auto">
        <div className="grid grid-cols-[130px_1fr_120px_auto] gap-3 px-4.5 px-5 py-3 bg-surface-2 font-mono text-[11px] uppercase text-ink-3 min-w-[560px]">
          <span>Fecha</span>
          <span>Nombre</span>
          <span>Tipo</span>
          <span>Acciones</span>
        </div>
        {loadingHolidays && <p className="px-5 py-4 text-[13.5px] text-ink-3">Cargando festivos…</p>}
        {!loadingHolidays &&
          holidays.map((h) => (
            <div key={h.id} className="border-t border-line min-w-[560px]">
              {editingHolidayId === h.id ? (
                <div className="p-4.5 px-5 space-y-3 bg-surface-2 animate-rise">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="label">Fecha</label>
                      <input
                        type="date"
                        className="field font-mono"
                        value={editDate}
                        onChange={(e) => setEditDate(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="label">Nombre</label>
                      <input className="field" value={editName} onChange={(e) => setEditName(e.target.value)} />
                    </div>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <button
                      type="button"
                      onClick={saveHolidayEdit}
                      disabled={!editDate || !editName.trim() || savingHolidayId === h.id}
                      className="btn-primary h-10 px-4 text-[13.5px] max-w-[200px]"
                    >
                      {savingHolidayId === h.id ? 'Guardando…' : 'Guardar'}
                    </button>
                    <button
                      type="button"
                      onClick={closeEditHoliday}
                      disabled={savingHolidayId === h.id}
                      className="h-10 px-4 rounded-xl border border-line-strong font-semibold text-[13.5px]"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-[130px_1fr_120px_auto] gap-3 px-5 py-3 items-center">
                  <span className="font-mono text-[13px]">{h.date}</span>
                  <span className="text-[14px] truncate">{h.name}</span>
                  <span className="text-[12.5px] text-ink-2">{TYPE_LABELS[h.holiday_type] ?? h.holiday_type}</span>
                  <div className="flex items-center gap-2 justify-end">
                    <button
                      type="button"
                      onClick={() => openEditHoliday(h)}
                      className="h-8 px-2.5 rounded-lg border border-line-strong font-semibold text-[12px] flex items-center gap-1"
                    >
                      <Icon name="edit" size={13} strokeWidth={1.8} />
                      Editar
                    </button>
                    <button
                      type="button"
                      onClick={() => removeHoliday(h)}
                      disabled={deletingHolidayId === h.id}
                      className="h-8 px-2.5 rounded-lg border border-danger text-danger font-semibold text-[12px] disabled:opacity-40"
                    >
                      {deletingHolidayId === h.id ? '…' : 'Borrar'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        {!loadingHolidays && holidays.length === 0 && (
          <div className="p-2">
            <EmptyState
              icon="calendar"
              title="Este calendario no tiene festivos"
              body="Siémbralo desde la lista de la izquierda o añade uno a mano."
            />
          </div>
        )}
      </div>
    </div>
  );
}
